import { createContext, useContext, useCallback, type ReactNode } from "react";
import { trpc } from "@client/trpc";
import { roundEntryHref } from "@client/roundRoutePaths";
import { useEffectiveUser } from "./use-effective-user";
import { useNetworkStatus } from "./useNetworkStatus";

const RoundEntryHrefContext = createContext<(path: string) => string>(
  (path) => path,
);

export function useRoundEntryHref() {
  return useContext(RoundEntryHrefContext);
}

/** Fetch once at an entry surface; rows only consume link presentation. */
export function useRoundAddressHref() {
  const { data: user } = useEffectiveUser();
  const viewerId = user?.userId;
  const { isOnline } = useNetworkStatus();
  const { data, isError, isFetchedAfterMount, isFetching } =
    trpc.getRoundSlugs.useQuery(
      { viewerId: viewerId ?? "" },
      {
        enabled: !!viewerId && isOnline,
        staleTime: 0,
        refetchOnMount: "always",
        retry: 1,
      },
    );
  // An alias may have been released/reassigned since persistence. Online
  // entries use IDs until this mount has checked the current owner registry.
  const canUseAddresses =
    !!viewerId &&
    !isError &&
    (!isOnline || (isFetchedAfterMount && !isFetching));
  const href = useCallback(
    (path: string) => roundEntryHref(path, canUseAddresses ? (data ?? []) : []),
    [data, canUseAddresses],
  );
  return href;
}

export function RoundEntryLinksProvider({ children }: { children: ReactNode }) {
  const href = useRoundAddressHref();
  return (
    <RoundEntryHrefContext.Provider value={href}>
      {children}
    </RoundEntryHrefContext.Provider>
  );
}
